import { FC, useState } from "react";
import { Button, message, Modal, Table, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import axiosInstance from "../axiosInstance";
import { getCollectionFields } from "../services/collectionFields";
import { transformToApiQueryParams } from "../hooks/useQueryParamsToState";

interface YearData {
  avg_prix: number;
  avg_svb_verif: number;
  max_prix: number;
  max_svb_verif: number;
  median_prix: number;
  median_svb_verif: number;
  median_prix_per_m2: number;
  min_prix_per_m2: number;
  max_prix_per_m2: number;
  min_prix: number;
  min_svb_verif: number;
  nb_trans: number;
  volume: number;
  avg_prix_m2: number;
}

interface StatsData {
  avg_prix: number;
  avg_svb_verif: number;
  max_prix: number;
  max_svb_verif: number;
  median_prix: number;
  median_svb_verif: number;
  median_prix_per_m2: number;
  min_prix_per_m2: number;
  max_prix_per_m2: number;
  min_prix: number;
  min_svb_verif: number;
  nb_trans: number;
  volume: number;
  avg_prix_m2: number;
  years_data: Record<string, YearData>;
}

// Helper component for info tooltips
const InfoTooltip: FC<{ title: string; color?: string }> = ({ title, color = "#1890ff" }) => (
  <Tooltip title={title} placement="right">
    <InfoCircleOutlined style={{ marginLeft: 8, color, cursor: "help" }} />
  </Tooltip>
);

const Statistiques: FC<{ filters: Record<string, string[]> }> = ({
  filters,
}) => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [statsData, setStatsData] = useState<StatsData | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const handleFetchStats = async () => {
    setLoading(true);
    try {
      const collectionId = "transactions_fao";

      const fields = await getCollectionFields(collectionId);
      const apiParams = transformToApiQueryParams(
        fields,
        new URL(window.location.href).searchParams
      );

      const response = await axiosInstance.post(
        `https://melcarnebackend.digipulse.ch/get_stats_admin_list`,
        { filters: JSON.parse(apiParams.get("filter")!) }
      );

      setStatsData(response.data);
      setModalVisible(true); // Open modal with stats

      messageApi.open({
        type: "success",
        content: "Les statistiques ont été récupérées avec succès.",
      });
    } catch (error) {
      console.log(error);
      messageApi.open({
        type: "error",
        content: "Une erreur est survenue lors de la demande de statistiques.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}

      {/* Fetch Stats Button */}
      <Button
        style={{ backgroundColor: "#fee08b" }}
        onClick={handleFetchStats}
        loading={loading}
      >
        {`Statistiques sur cette recherche`}
      </Button>

      {/* Modal for Stats */}
      <Modal
        title="Statistiques de la recherche"
        width="80vw"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Fermer
          </Button>,
        ]}
      >
        {statsData ? (
          <div>
            <p>
              <strong>Nombre de transactions :</strong>{" "}
              {statsData.nb_trans.toLocaleString()}
              <InfoTooltip 
                title="🟢 Nombre de transactions de la recherche, y compris QPT et Prix non représentatif" 
                color="#52c41a" 
              />
            </p>
            <p>
              <strong>Volume total :</strong>{" "}
              {Math.floor(statsData.volume).toLocaleString()} CHF
              <InfoTooltip 
                title="🟢 Somme de tous les prix des transactions de la recherche, y compris QPT et Prix non représentatif" 
                color="#52c41a" 
              />
            </p>
            <hr />
            <p>
              <strong>Prix minimum :</strong>{" "}
              {Math.floor(statsData.min_prix).toLocaleString()} CHF
              <InfoTooltip 
                title="🔴 Prix minimum des transactions SANS QPT et SANS Prix non représentatif" 
                color="#ff4d4f" 
              />
            </p>
            <p>
              <strong>Prix moyen :</strong>{" "}
              {Math.floor(statsData.avg_prix).toLocaleString()} CHF
              <InfoTooltip 
                title="🔴 Prix moyen des transactions SANS QPT et SANS Prix non représentatif" 
                color="#ff4d4f" 
              />
            </p>
            <p>
              <strong>Prix médian :</strong>{" "}
              {Math.floor(statsData.median_prix).toLocaleString()} CHF
              <InfoTooltip 
                title="🔴 Prix médian des transactions SANS QPT et SANS Prix non représentatif" 
                color="#ff4d4f" 
              />
            </p>
            <p>
              <strong>Prix maximum :</strong>{" "}
              {Math.floor(statsData.max_prix).toLocaleString()} CHF
              <InfoTooltip 
                title="🔴 Prix maximum des transactions SANS QPT et SANS Prix non représentatif" 
                color="#ff4d4f" 
              />
            </p>
            <hr />
            <p>
              <strong>SVB Vérif minimum :</strong>{" "}
              {Math.floor(statsData.min_svb_verif).toLocaleString()} m2
              <InfoTooltip 
                title="🔵 SVB minimum des transactions avec status 'SVB Vérifiée', y compris QPT et Prix non représentatif" 
                color="#1890ff" 
              />
            </p>
            <p>
              <strong>SVB Vérif moyen :</strong>{" "}
              {Math.floor(statsData.avg_svb_verif).toLocaleString()} m2
              <InfoTooltip 
                title="🔵 SVB moyen des transactions avec status 'SVB Vérifiée', y compris QPT et Prix non représentatif" 
                color="#1890ff" 
              />
            </p>
            <p>
              <strong>SVB Vérif médian :</strong>{" "}
              {Math.floor(statsData.median_svb_verif).toLocaleString()} m2
              <InfoTooltip 
                title="🔵 SVB médian des transactions avec status 'SVB Vérifiée', y compris QPT et Prix non représentatif" 
                color="#1890ff" 
              />
            </p>
            <p>
              <strong>SVB Vérif maximum :</strong>{" "}
              {Math.floor(statsData.max_svb_verif).toLocaleString()} m2
              <InfoTooltip 
                title="🔵 SVB maximum des transactions avec status 'SVB Vérifiée', y compris QPT et Prix non représentatif" 
                color="#1890ff" 
              />
            </p>
            <hr />
            <p>
              <strong>Prix/m2 moyen :</strong>{" "}
              {statsData.avg_prix_m2 !== null ? Math.floor(statsData.avg_prix_m2).toLocaleString() + " CHF/m2" : "N/A"}
              <InfoTooltip 
                title="🟣 Prix/m2 moyen calculé comme Prix ÷ SVB Vérifiée, uniquement pour les transactions avec status 'SVB Vérifiée', SANS QPT et SANS Prix non représentatif" 
                color="#722ed1" 
              />
            </p>
            <p>
              <strong>Prix/m2 median :</strong>{" "}
              {statsData.median_prix_per_m2 !== null ? Math.floor(statsData.median_prix_per_m2).toLocaleString() + " CHF/m2" : "N/A"}
              <InfoTooltip 
                title="🟣 Prix/m2 médian calculé comme Prix ÷ SVB Vérifiée, uniquement pour les transactions avec status 'SVB Vérifiée', SANS QPT et SANS Prix non représentatif" 
                color="#722ed1" 
              />
            </p>
            <p>
              <strong>Prix/m2 min :</strong>{" "}
              {statsData.min_prix_per_m2 !== null ? Math.floor(statsData.min_prix_per_m2).toLocaleString() + " CHF/m2" : "N/A"}
              <InfoTooltip 
                title="🟣 Prix/m2 minimum calculé comme Prix ÷ SVB Vérifiée, uniquement pour les transactions avec status 'SVB Vérifiée', SANS QPT et SANS Prix non représentatif" 
                color="#722ed1" 
              />
            </p>
            <p>
              <strong>Prix/m2 max :</strong>{" "}
              {statsData.max_prix_per_m2 !== null ? Math.floor(statsData.max_prix_per_m2).toLocaleString() + " CHF/m2" : "N/A"}
              <InfoTooltip 
                title="🟣 Prix/m2 maximum calculé comme Prix ÷ SVB Vérifiée, uniquement pour les transactions avec status 'SVB Vérifiée', SANS QPT et SANS Prix non représentatif" 
                color="#722ed1" 
              />
            </p>
          </div>
        ) : (
          <p>Chargement des statistiques...</p>
        )}

        {/* Yearly Data */}
        {statsData?.years_data && (
          <div style={{ marginTop: 16 }}>
            <h1 style={{ fontWeight: "bold" }}>Données par année</h1>
            <Table
              dataSource={Object.entries(statsData.years_data).map(
                ([year, data]) => ({
                  key: year,
                  year,
                  ...data,
                })
              )}
              columns={[
                {
                  title: "Année",
                  dataIndex: "year",
                  key: "year",
                  fixed: "left",
                },
                {
                  title: (
                    <span>
                      Nombre de transactions
                      <InfoTooltip 
                        title="🟢 Toutes les transactions de cette année apparaîssant dans la recherche, y compris QPT et Prix non représentatif" 
                        color="#52c41a" 
                      />
                    </span>
                  ),
                  dataIndex: "nb_trans",
                  key: "nb_trans",
                },
                {
                  title: (
                    <span>
                      Volume
                      <InfoTooltip 
                        title="🟢 Somme de tous les prix des transactions de cette année apparaîssant dans la recherche, y compris QPT et Prix non représentatif" 
                        color="#52c41a" 
                      />
                    </span>
                  ),
                  dataIndex: "volume",
                  key: "volume",
                  render: (value) =>
                    Math.floor(value).toLocaleString() + " CHF",
                },

                {
                  title: (
                    <span>
                      Prix min
                      <InfoTooltip 
                        title="🔴 Prix minimum des transactions de cette année apparaîssant dans la recherche SANS QPT et SANS Prix non représentatif" 
                        color="#ff4d4f" 
                      />
                    </span>
                  ),
                  dataIndex: "min_prix",
                  key: "min_prix",
                  render: (value) =>
                    Math.floor(value).toLocaleString() + " CHF",
                },
                {
                  title: (
                    <span>
                      Prix moyen
                      <InfoTooltip 
                        title="🔴 Prix moyen des transactions de cette année apparaîssant dans la recherche SANS QPT et SANS Prix non représentatif" 
                        color="#ff4d4f" 
                      />
                    </span>
                  ),
                  dataIndex: "avg_prix",
                  key: "avg_prix",
                  render: (value) =>
                    Math.floor(value).toLocaleString() + " CHF",
                },
                {
                  title: (
                    <span>
                      Prix médian
                      <InfoTooltip 
                        title="🔴 Prix médian des transactions de cette année apparaîssant dans la recherche SANS QPT et SANS Prix non représentatif" 
                        color="#ff4d4f" 
                      />
                    </span>
                  ),
                  dataIndex: "median_prix",
                  key: "median_prix",
                  render: (value) =>
                    Math.floor(value).toLocaleString() + " CHF",
                },
                {
                  title: (
                    <span>
                      Prix max
                      <InfoTooltip 
                        title="🔴 Prix maximum des transactions de cette année apparaîssant dans la recherche SANS QPT et SANS Prix non représentatif" 
                        color="#ff4d4f" 
                      />
                    </span>
                  ),
                  dataIndex: "max_prix",
                  key: "max_prix",
                  render: (value) =>
                    Math.floor(value).toLocaleString() + " CHF",
                },

                {
                  title: (
                    <span>
                      SVB v. min
                      <InfoTooltip 
                        title="🔵 SVB minimum des transactions de cette année apparaîssant dans la recherche avec status 'SVB Vérifiée', y compris QPT et Prix non représentatif" 
                        color="#1890ff" 
                      />
                    </span>
                  ),
                  dataIndex: "min_svb_verif",
                  key: "min_svb_verif",
                  render: (value) => Math.floor(value).toLocaleString() + " m2",
                },
                {
                  title: (
                    <span>
                      SVB v. moyen
                      <InfoTooltip 
                        title="🔵 SVB moyen des transactions de cette année apparaîssant dans la recherche avec status 'SVB Vérifiée', y compris QPT et Prix non représentatif" 
                        color="#1890ff" 
                      />
                    </span>
                  ),
                  dataIndex: "avg_svb_verif",
                  key: "avg_svb_verif",
                  render: (value) => Math.floor(value).toLocaleString() + " m2",
                },
                {
                  title: (
                    <span>
                      SVB v. médian
                      <InfoTooltip 
                        title="🔵 SVB médian des transactions de cette année apparaîssant dans la recherche avec status 'SVB Vérifiée', y compris QPT et Prix non représentatif" 
                        color="#1890ff" 
                      />
                    </span>
                  ),
                  dataIndex: "median_svb_verif",
                  key: "median_svb_verif",
                  render: (value) => Math.floor(value).toLocaleString() + " m2",
                },
                {
                  title: (
                    <span>
                      SVB v. max
                      <InfoTooltip 
                        title="🔵 SVB maximum des transactions de cette année apparaîssant dans la recherche avec status 'SVB Vérifiée', y compris QPT et Prix non représentatif" 
                        color="#1890ff" 
                      />
                    </span>
                  ),
                  dataIndex: "max_svb_verif",
                  key: "max_svb_verif",
                  render: (value) => Math.floor(value).toLocaleString() + " m2",
                },

                {
                  title: (
                    <span>
                      Prix/m2 moyen
                      <InfoTooltip 
                        title="🟣 Prix/m2 moyen calculé comme Prix ÷ SVB Vérifiée pour cette année, uniquement transactions apparaîssant dans la recherche avec status 'SVB Vérifiée', SANS QPT et SANS Prix non représentatif" 
                        color="#722ed1" 
                      />
                    </span>
                  ),
                  dataIndex: "avg_prix_m2",
                  key: "avg_prix_m2",
                  render: (value) =>
                    value !== null ? Math.floor(value).toLocaleString() + " CHF/m2" : "N/A",
                },
                {
                  title: (
                    <span>
                      Prix/m2 median
                      <InfoTooltip 
                        title="🟣 Prix/m2 médian calculé comme Prix ÷ SVB Vérifiée pour cette année, uniquement transactions apparaîssant dans la recherche avec status 'SVB Vérifiée', SANS QPT et SANS Prix non représentatif" 
                        color="#722ed1" 
                      />
                    </span>
                  ),
                  dataIndex: "median_prix_per_m2",
                  key: "median_prix_per_m2",
                  render: (value) =>
                    value !== null ? Math.floor(value).toLocaleString() + " CHF/m2" : "N/A",
                },
                {
                  title: (
                    <span>
                      Prix/m2 min
                      <InfoTooltip 
                        title="🟣 Prix/m2 minimum calculé comme Prix ÷ SVB Vérifiée pour cette année, uniquement transactions apparaîssant dans la recherche avec status 'SVB Vérifiée', SANS QPT et SANS Prix non représentatif" 
                        color="#722ed1" 
                      />
                    </span>
                  ),
                  dataIndex: "min_prix_per_m2",
                  key: "min_prix_per_m2",
                  render: (value) =>
                    value !== null ? Math.floor(value).toLocaleString() + " CHF/m2" : "N/A",
                },
                {
                  title: (
                    <span>
                      Prix/m2 max
                      <InfoTooltip 
                        title="🟣 Prix/m2 maximum calculé comme Prix ÷ SVB Vérifiée pour cette année, uniquement transactions apparaîssant dans la recherche avec status 'SVB Vérifiée', SANS QPT et SANS Prix non représentatif" 
                        color="#722ed1" 
                      />
                    </span>
                  ),
                  dataIndex: "max_prix_per_m2",
                  key: "max_prix_per_m2",
                  render: (value) =>
                    value !== null ? Math.floor(value).toLocaleString() + " CHF/m2" : "N/A",
                },
              ]}
              pagination={false}
              scroll={{ x: "max-content" }} // Enables horizontal scroll
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default Statistiques;
